import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getRegion, RegionCode } from '../utils/region';
const areaCodes = [{
  code: '253',
  src: 'https://cdn.magicpatterns.com/uploads/68rE1RFxKTaMLnZX9bTQ6m/253.png',
  duration: 20000
}, {
  code: '206',
  src: 'https://cdn.magicpatterns.com/uploads/6RRJ8Cb2MtYWCN2KCSKtBN/206.png',
  duration: 500
}, {
  code: '425',
  src: 'https://cdn.magicpatterns.com/uploads/g4GXqv7wQ4H6HHSd1TBZ5s/425.png',
  duration: 500
}];
const GRAVEL_SRC = 'https://cdn.magicpatterns.com/uploads/tqXEcbNCcYC2dBxg9zT8wG/GRAVEL.png';
// TEMPORARY GLOBAL HIDE — every logo across the site is suppressed
// while this flag is true (the Logo component below, plus the inline
// <img> logos in Navbar.tsx that import this constant). To restore the
// logos, flip this back to `false` and re-publish.
export const LOGOS_HIDDEN = false;
interface LogoProps {
  className?: string;
  imgClassName?: string;
  invertOnLight?: boolean;
  /**
   * Show every Nth full cycle. 1 = animate every cycle (default, used by navbar).
   * 3 = only animate every 3rd cycle (used by footer).
   * Between rotations, the lead code holds for `cycleEvery * (full cycle duration)`.
   */
  cycleEvery?: number;
  /**
   * Which region's area code leads the lockup. Defaults to the active region
   * inferred from the hostname (253 / 206 / 425). The rotation order is
   * reordered so the visitor's region always appears first.
   */
  region?: RegionCode;
  /**
   * Pin the wordmark to a single region's area code — no rotation. Used by the
   * navbar so the visitor's domain reads as a fixed "{region}GRAVEL" identity.
   */
  pinned?: boolean;
}
/**
 * Logo lockup with a static "Gravel" wordmark layered on top of an area code.
 * - pinned: shows only the region's area code (no rotation) — navbar identity.
 * - rotating: leads with the region's code, then cycles through the others —
 *   footer flourish that shows the company owns all three metros.
 */
export function Logo({
  className = '',
  imgClassName = 'h-10 w-auto',
  invertOnLight = true,
  cycleEvery = 1,
  region,
  pinned = false
}: LogoProps) {
  // While LOGOS_HIDDEN is true, every Logo instance renders nothing.
  // Flip the constant above to `false` (or remove this block) to restore.
  if (LOGOS_HIDDEN) return null;
  // Reorder the area-code sequence so the active region leads. The rest keep
  // their natural order behind it, so 206 → [206, 253, 425], etc.
  const sequence = useMemo(() => {
    const active = region ?? getRegion();
    const lead = areaCodes.filter(a => a.code === active);
    const rest = areaCodes.filter(a => a.code !== active);
    return lead.length ? [...lead, ...rest] : areaCodes;
  }, [region]);
  const [index, setIndex] = useState(0);
  // Reset to the lead code whenever the sequence changes (region switch).
  useEffect(() => {
    setIndex(0);
  }, [sequence]);
  useEffect(() => {
    if (pinned) return; // No timer when pinned to a single region.
    const baseDuration = sequence[index].duration;
    const duration = index === 0 ? baseDuration * cycleEvery : baseDuration;
    const timeout = setTimeout(() => {
      setIndex(i => (i + 1) % sequence.length);
    }, duration);
    return () => clearTimeout(timeout);
  }, [index, cycleEvery, pinned, sequence]);
  const current = pinned ? sequence[0] : sequence[index];
  const invertClass = invertOnLight ? 'dark:brightness-100 brightness-0' : '';
  return <span className={`relative inline-block overflow-hidden ${className}`} aria-label={`${current.code}Gravel`} data-id="element-1156">
      {/* Sizer: GRAVEL defines the layout dimensions and stays static on top */}
      <img src={GRAVEL_SRC} alt="" aria-hidden="true" className={`${imgClassName} ${invertClass} block relative z-10 pointer-events-none`} data-id="element-1157" />

      {/* Rotating area code, layered underneath GRAVEL with slide-up transition */}
      <AnimatePresence initial={false} data-id="element-1158">
        <motion.img key={current.code} src={current.src} alt={current.code} className={`${imgClassName} ${invertClass} absolute inset-0 z-0`} initial={{
        opacity: 1
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 1
      }} transition={{
        duration: 0
      }} data-id="element-1159" />
      </AnimatePresence>
    </span>;
}